import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, XStack, YStack } from 'tamagui';

import EventCard from '@/components/EventCard';
import type { ShortEvent } from '@/src/api/eventsApi';

/**
 * ── /components/event-card ───────────────────────────────────────────────
 *
 * EventCard — the large event tile drawn on the home screen (`app/index.tsx`)
 * in three different layouts, all reproduced below as their own captioned
 * section. The card is render-only: it's handed a `ShortEvent` and picks its
 * own primary image, formats its own slot-summary line (via
 * `formatSlotSummary`, see `src/utils/dateFormatter.ts`), and reads its own
 * `isSoldOut`/`isInPast` to decide the grayscale + badge treatment — the
 * caller never pre-computes any of that.
 *
 * Its full prop surface (`components/EventCard.tsx`): `event` (required),
 * `onPress`, `width` (defaults to 240, the horizontal-carousel size — the
 * grid passes its own measured column width instead), `panelBgColor` (only
 * `'$panelTranslucent'` or `'$backgroundSecondary'`, default
 * `'$backgroundSecondary'` — the neutral recessed panel; `'$panelTranslucent'`
 * is the dark-band Access Pass treatment), and `textColor` (`'dark'` default
 * or `'light'`, paired 1:1 with `panelBgColor` at every real call site —
 * `'$panelTranslucent'` always ships `textColor="light"` since that panel
 * sits on `$brandDark`).
 * ─────────────────────────────────────────────────────────────────────────
 */

// Deliberately mixed title lengths — a one-word title next to one that wraps
// to multiple lines — to eyeball how a stretched row / wrapping grid handles
// uneven card heights. Typed as the API's `ShortEvent` since that's what the
// card takes now: raw images/venue/slots, not pre-formatted display strings.
const PREVIEW_CAROUSEL: ShortEvent[] = [
  {
    id: 'c1',
    title: 'Brunch',
    isInPast: false,
    isSoldOut: false,
    images: [
      {
        id: 'c1-img',
        url: 'https://picsum.photos/id/1080/600/800',
        isPrimary: true,
      },
    ],
    venue: { name: 'Kala Ghoda', googleMapsUrl: '' },
    kind: 'ticketed',
    category: 'normal',
    nextBookableSlot: {
      startDatetime: '2026-08-12T11:00:00',
      endDatetime: '2026-08-12T13:00:00',
    },
    lastBookableSlot: {
      startDatetime: '2026-08-12T11:00:00',
      endDatetime: '2026-08-12T13:00:00',
    },
    externalUrl: null,
    orgDetails: null,
    priceFrom: {
      amountInMinorUnits: 150000,
      currency: 'INR',
      display: '₹1,500',
    },
  },
  {
    id: 'c2',
    title: 'An Evening of Sufi Qawwali & Slow-Cooked Awadhi Dinner on the Lawn',
    images: [
      {
        id: 'c2-img',
        url: 'https://picsum.photos/id/1069/600/800',
        isPrimary: true,
      },
    ],
    venue: { name: 'The Terrace, Bandra', googleMapsUrl: '' },
    kind: 'ticketed',
    category: 'normal',
    nextBookableSlot: {
      startDatetime: '2026-08-12T19:30:00',
      endDatetime: '2026-08-12T22:30:00',
    },
    lastBookableSlot: {
      startDatetime: '2026-08-14T19:30:00',
      endDatetime: '2026-08-14T22:30:00',
    },
    isSoldOut: false,
    isInPast: false,
    externalUrl: null,
    orgDetails: null,
    priceFrom: {
      amountInMinorUnits: 350000,
      currency: 'INR',
      display: '₹3,500',
    },
  },
  {
    id: 'c3',
    title: 'Pottery Workshop',
    images: [
      {
        id: 'c3-img',
        url: 'https://picsum.photos/id/1043/600/800',
        isPrimary: true,
      },
    ],
    venue: { name: 'Versova', googleMapsUrl: '' },
    kind: 'ticketed',
    category: 'normal',
    nextBookableSlot: {
      startDatetime: '2026-08-13T16:00:00',
      endDatetime: '2026-08-13T18:00:00',
    },
    lastBookableSlot: {
      startDatetime: '2026-08-13T16:00:00',
      endDatetime: '2026-08-13T18:00:00',
    },
    isSoldOut: false,
    isInPast: false,
    externalUrl: null,
    orgDetails: null,
    priceFrom: {
      amountInMinorUnits: 120000,
      currency: 'INR',
      display: '₹1,200',
    },
  },
  {
    // The sold-out card: grayscale image, dimmed panel, "Sold out" badge.
    id: 'c4',
    title: 'Late-Night Comedy & Cocktails',
    images: [
      {
        id: 'c4-img',
        url: 'https://picsum.photos/id/1050/600/800',
        isPrimary: true,
      },
    ],
    venue: { name: 'Lower Parel', googleMapsUrl: '' },
    kind: 'ticketed',
    category: 'normal',
    nextBookableSlot: {
      startDatetime: '2026-08-13T21:00:00',
      endDatetime: '2026-08-13T23:30:00',
    },
    lastBookableSlot: {
      startDatetime: '2026-08-13T21:00:00',
      endDatetime: '2026-08-13T23:30:00',
    },
    isSoldOut: true,
    isInPast: false,
    externalUrl: null,
    orgDetails: null,
    priceFrom: { amountInMinorUnits: 90000, currency: 'INR', display: '₹900' },
  },
];

// Preview-only 2-column grid geometry. Fixed width so the column math is
// deterministic here; the real grid (app/index.tsx's `gridColW`, via
// `useFrameWidth`) measures its container instead. Gutter is $md (12),
// matching the grid gap below.
const GRID_W = 360;
const GRID_COL_W = (GRID_W - 12) / 2;

// A relative-day, LOCAL-time ISO datetime — used to build the today/tomorrow/
// future slot-summary fixtures below so their date labels always read as
// "Today"/"Tomorrow"/etc. relative to whenever this preview actually runs.
// No trailing Z: parseISO reads a bare string as local time (see
// dateFormatter's own note), so this is built by hand rather than via
// toISOString, which would shift to UTC and could land on the wrong day near
// midnight.
const slotIso = (daysOut: number, hour = 19) => {
  const d = new Date();
  d.setDate(d.getDate() + daysOut);
  d.setHours(hour, 30, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(hour)}:30:00`;
};

// Shared base for every date-label fixture below — only the id/title/slots
// vary per entry, everything else (image, venue, price) is filler that
// formatSlotSummary doesn't touch.
const SLOT_SUMMARY_BASE = {
  images: [
    {
      id: 'slot-summary-img',
      url: 'https://picsum.photos/id/1074/600/800',
      isPrimary: true,
    },
  ],
  venue: { name: 'Test Venue', googleMapsUrl: '' },
  kind: 'ticketed' as const,
  category: 'normal' as const,
  isSoldOut: false,
  externalUrl: null,
  orgDetails: null,
  priceFrom: { amountInMinorUnits: 150000, currency: 'INR', display: '₹1,500' },
};

// One entry per distinct date-label SHAPE `formatSlotSummary` can produce.
// Today/Tomorrow cases are built relative to now via `slotIso` (the point IS
// the run-relative label); the calendar-boundary cases (month, year, next
// year) use FIXED dates instead, since there the point is the boundary
// itself, not the run date.
const SLOT_SUMMARY_STATES: { id: string; label: string; event: ShortEvent }[] =
  [
    {
      id: 'ss-today-one',
      label: 'One slot, today',
      event: {
        ...SLOT_SUMMARY_BASE,
        id: 'ss-today-one',
        title: 'Rooftop Jazz Night',
        isInPast: false,
        nextBookableSlot: {
          startDatetime: slotIso(0, 19),
          endDatetime: slotIso(0, 21),
        },
        lastBookableSlot: {
          startDatetime: slotIso(0, 19),
          endDatetime: slotIso(0, 21),
        },
      },
    },
    {
      id: 'ss-tomorrow-one',
      label: 'One slot, tomorrow',
      event: {
        ...SLOT_SUMMARY_BASE,
        id: 'ss-tomorrow-one',
        title: 'Sunset Wine Tasting',
        isInPast: false,
        nextBookableSlot: {
          startDatetime: slotIso(1, 19),
          endDatetime: slotIso(1, 21),
        },
        lastBookableSlot: {
          startDatetime: slotIso(1, 19),
          endDatetime: slotIso(1, 21),
        },
      },
    },
    {
      id: 'ss-today-multi',
      label: 'Multiple slots, all today',
      event: {
        ...SLOT_SUMMARY_BASE,
        id: 'ss-today-multi',
        title: 'Pottery Drop-in Sessions',
        isInPast: false,
        nextBookableSlot: {
          startDatetime: slotIso(0, 11),
          endDatetime: slotIso(0, 13),
        },
        lastBookableSlot: {
          startDatetime: slotIso(0, 20),
          endDatetime: slotIso(0, 22),
        },
      },
    },
    {
      id: 'ss-tomorrow-multi',
      label: 'Multiple slots, all tomorrow',
      event: {
        ...SLOT_SUMMARY_BASE,
        id: 'ss-tomorrow-multi',
        title: 'Brunch Seatings',
        isInPast: false,
        nextBookableSlot: {
          startDatetime: slotIso(1, 11),
          endDatetime: slotIso(1, 13),
        },
        lastBookableSlot: {
          startDatetime: slotIso(1, 20),
          endDatetime: slotIso(1, 22),
        },
      },
    },
    {
      id: 'ss-future-one',
      label: 'One slot, after tomorrow',
      event: {
        ...SLOT_SUMMARY_BASE,
        id: 'ss-future-one',
        title: 'Comedy Night',
        isInPast: false,
        nextBookableSlot: {
          startDatetime: slotIso(5, 19),
          endDatetime: slotIso(5, 21),
        },
        lastBookableSlot: {
          startDatetime: slotIso(5, 19),
          endDatetime: slotIso(5, 21),
        },
      },
    },
    {
      id: 'ss-future-multi-sameday',
      label: 'Multiple slots, after tomorrow, all on one day',
      event: {
        ...SLOT_SUMMARY_BASE,
        id: 'ss-future-multi-sameday',
        title: 'Pizza-Making Studio',
        isInPast: false,
        nextBookableSlot: {
          startDatetime: slotIso(5, 11),
          endDatetime: slotIso(5, 13),
        },
        lastBookableSlot: {
          startDatetime: slotIso(5, 20),
          endDatetime: slotIso(5, 22),
        },
      },
    },
    {
      id: 'ss-today-to-3days',
      label: 'Multiple slots, spanning today through 3 days out',
      event: {
        ...SLOT_SUMMARY_BASE,
        id: 'ss-today-to-3days',
        title: 'Live Music Weekend',
        isInPast: false,
        nextBookableSlot: {
          startDatetime: slotIso(0, 19),
          endDatetime: slotIso(0, 21),
        },
        lastBookableSlot: {
          startDatetime: slotIso(3, 19),
          endDatetime: slotIso(3, 21),
        },
      },
    },
    {
      // Adjacent to the range above: the near end is "Tomorrow" rather than
      // "Today", checking that a relative word on one end and a plain date
      // on the other still reads fine ("Tomorrow – Fri, 4 Sep") rather than
      // looking like a typo.
      id: 'ss-tomorrow-to-future',
      label: 'Multiple slots, spanning tomorrow through a later day',
      event: {
        ...SLOT_SUMMARY_BASE,
        id: 'ss-tomorrow-to-future',
        title: 'Weekend Flea Market',
        isInPast: false,
        nextBookableSlot: {
          startDatetime: slotIso(1, 10),
          endDatetime: slotIso(1, 18),
        },
        lastBookableSlot: {
          startDatetime: slotIso(6, 10),
          endDatetime: slotIso(6, 18),
        },
      },
    },
    {
      // Fixed, not relative — the point is the Aug→Sep boundary itself.
      // Shifted a day off the 31st so it doesn't land on "today" and
      // collapse the label.
      id: 'ss-month-cross',
      label: 'Multiple slots, spanning a month boundary (31 Aug – 3 Sep)',
      event: {
        ...SLOT_SUMMARY_BASE,
        id: 'ss-month-cross',
        title: "Chef's Table Residency",
        isInPast: false,
        nextBookableSlot: {
          startDatetime: '2026-08-31T19:30:00',
          endDatetime: '2026-08-31T21:30:00',
        },
        lastBookableSlot: {
          startDatetime: '2026-09-03T19:30:00',
          endDatetime: '2026-09-03T21:30:00',
        },
      },
    },
    {
      // The year boundary is a genuinely different shape from "all next
      // year" below — only ONE end of the range gets the year suffix.
      id: 'ss-year-cross',
      label: 'Multiple slots, spanning the year boundary (30 Dec – 2 Jan)',
      event: {
        ...SLOT_SUMMARY_BASE,
        id: 'ss-year-cross',
        title: "New Year's Eve Supper Club",
        isInPast: false,
        nextBookableSlot: {
          startDatetime: '2026-12-30T19:30:00',
          endDatetime: '2026-12-30T23:30:00',
        },
        lastBookableSlot: {
          startDatetime: '2027-01-02T19:30:00',
          endDatetime: '2027-01-02T23:30:00',
        },
      },
    },
    {
      id: 'ss-next-year-multi',
      label: 'Multiple slots, all next year (different days)',
      event: {
        ...SLOT_SUMMARY_BASE,
        id: 'ss-next-year-multi',
        title: 'Spring Food Festival',
        isInPast: false,
        nextBookableSlot: {
          startDatetime: '2027-03-10T18:00:00',
          endDatetime: '2027-03-10T22:00:00',
        },
        lastBookableSlot: {
          startDatetime: '2027-03-14T18:00:00',
          endDatetime: '2027-03-14T22:00:00',
        },
      },
    },
    {
      id: 'ss-next-year-sameday',
      label: 'Multiple slots, all next year, same day',
      event: {
        ...SLOT_SUMMARY_BASE,
        id: 'ss-next-year-sameday',
        title: 'Art Fair Preview',
        isInPast: false,
        nextBookableSlot: {
          startDatetime: '2027-03-10T11:00:00',
          endDatetime: '2027-03-10T13:00:00',
        },
        lastBookableSlot: {
          startDatetime: '2027-03-10T20:00:00',
          endDatetime: '2027-03-10T22:00:00',
        },
      },
    },
    {
      id: 'ss-past',
      label: 'Event in the past — grayscale image + "In past" badge',
      event: {
        ...SLOT_SUMMARY_BASE,
        id: 'ss-past',
        title: 'Winter Supper Club',
        isInPast: true,
        nextBookableSlot: {
          startDatetime: '2026-01-15T19:30:00',
          endDatetime: '2026-01-15T22:00:00',
        },
        lastBookableSlot: {
          startDatetime: '2026-01-15T19:30:00',
          endDatetime: '2026-01-15T22:00:00',
        },
      },
    },
  ];

export default function EventCardPreview() {
  return (
    <>
      <Head>
        <title>EventCard · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* Horizontal scroll row — home's carousels (Access Pass exclusive
            and Editorial Picks in `app/index.tsx`). An RN ScrollView (for the
            scroll) wraps a Tamagui XStack (for the tokened gap); no fixed
            `width` so every card sizes to its own content, and
            `items="stretch"` matches every card's height to the tallest in
            the row — c2's long title (which wraps to 3 lines) is what forces
            the panel to grow on its shorter neighbours. `panelBgColor`
            pairs with `textColor="light"` here — the Access Pass carousel's
            real shape, sitting on the dark `$brandDark` band. */}
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              Horizontal scroll row — home&apos;s carousels; no fixed width
              (cards size to content), items=&quot;stretch&quot; matches every
              card to the tallest
            </Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <XStack gap="$md" items="stretch">
                {PREVIEW_CAROUSEL.map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onPress={() => {}}
                    panelBgColor="$panelTranslucent"
                    textColor="light"
                  />
                ))}
              </XStack>
            </ScrollView>
          </YStack>

          {/* 2-column wrapping grid — the "All experiences" surface (home's
            FlatList grid, `renderExperience` in `app/index.tsx`, which
            passes its own measured `gridColW`). Same fixture as the
            carousel above, but every card gets a fixed `width` (here a
            deterministic preview stand-in for `useFrameWidth`'s measured
            column) and the default panel/text colors — the light-background
            "All experiences" grid, not the dark Access Pass band. Each wrap
            row is as tall as its own tallest card. */}
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              2-column wrapping grid — &quot;All experiences&quot;; fixed width
              per card, default panel colors, each wrap row as tall as its
              tallest card
            </Text>
            <XStack width={GRID_W} flexWrap="wrap" gap="$md" items="stretch">
              {PREVIEW_CAROUSEL.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  width={GRID_COL_W}
                  onPress={() => {}}
                />
              ))}
            </XStack>
          </YStack>

          {/* formatSlotSummary date-label states — one card per distinct
            shape the slot-summary line can take: today/tomorrow (single +
            multi-slot), a same-day multi-slot run further out, ranges
            starting today/tomorrow, a month boundary, a year boundary,
            next-year dates (ranged and same-day), and a past event. Default
            width/panel/text color throughout — only the slots and
            isInPast change per entry, isolating the date-label logic from
            the layout variations above. */}
          <YStack alignSelf="stretch" gap="$lg">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              formatSlotSummary date-label states — one card per distinct
              date-label shape
            </Text>
            {SLOT_SUMMARY_STATES.map((state) => (
              <YStack key={state.id} gap="$xs">
                <Text
                  fontFamily="$body"
                  fontSize="$small"
                  color="$textDarkMuted"
                >
                  {state.label}
                </Text>
                <EventCard event={state.event} onPress={() => {}} />
              </YStack>
            ))}
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
